import React, { useState, Children } from 'react';
import { PlusIcon, TrashIcon, ArrowLeftIcon, UserPlusIcon, MailIcon, BuildingIcon, GlobeIcon, SaveIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, BarChartIcon, CreditCardIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function VendorOnboarding({
  onSave,
  onSendInvite,
  setCurrentView,
  orgUnits = [],
  locale = 'en-US'
}) {
  const [onboardingMethod, setOnboardingMethod] = useState('direct');
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    country: '',
    taxId: '',
    website: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    postalCode: '',
    addressCountry: '',
    addressType: 'Physical',
    isPrimary: true,
    // Entity and site restrictions
    isGlobal: true,
    orgUnitId: null,
    includeChildOrgUnits: false,
    // ESG data
    esg: {
      score: '',
      environmental: '',
      social: '',
      governance: '',
      summary: ''
    },
    // Bank account fields
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    accountType: 'Checking'
  });
  const [inviteData, setInviteData] = useState({
    name: '',
    type: '',
    email: '',
    contactName: '',
    phone: '',
    role: '',
    message: 'We invite you to register as a vendor in our system. Please complete the onboarding process by following the link that will be sent to your email.'
  });
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [files, setFiles] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [showESGForm, setShowESGForm] = useState(false);
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGEditPermission] = useState(true); // In a real app, this would come from user permissions
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const vendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Service Provider', 'Consultant', 'Contractor', 'Other'];
  const addressTypes = ['Physical', 'Billing', 'Shipping', 'Mailing', 'Registered', 'Other'];
  const accountTypes = ['Checking', 'Savings', 'Current', 'Business', 'Corporate'];
  // Handle availability type change
  const handleAvailabilityChange = isGlobal => {
    setFormData({
      ...formData,
      isGlobal,
      // Clear org unit selections when switching to global
      orgUnitId: isGlobal ? null : formData.orgUnitId,
      includeChildOrgUnits: isGlobal ? false : formData.includeChildOrgUnits
    });
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name.startsWith('esg.')) {
      const esgField = name.split('.')[1];
      setFormData({
        ...formData,
        esg: {
          ...formData.esg,
          [esgField]: value
        }
      });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleInviteInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setInviteData({
      ...inviteData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addContact = () => {
    if (!formData.contactName || !formData.email) {
      setErrors({
        ...errors,
        contactName: !formData.contactName ? 'Contact name is required' : null,
        email: !formData.email ? 'Email is required' : null
      });
      return;
    }
    const newContact = {
      id: contacts.length + 1,
      name: formData.contactName,
      email: formData.email,
      phone: formData.phone || '',
      role: formData.role || ''
    };
    setContacts([...contacts, newContact]);
    setFormData({
      ...formData,
      contactName: '',
      email: '',
      phone: '',
      role: ''
    });
  };
  const removeContact = id => {
    setContacts(contacts.filter(contact => contact.id !== id));
  };
  const handleAddAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const newAddress = {
      id: editingAddress ? editingAddress.id : addresses.length + 1,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.addressCountry,
      addressType: formData.addressType,
      isPrimary: formData.isPrimary
    };
    let updatedAddresses = [];
    if (editingAddress) {
      // Update existing address
      updatedAddresses = addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // Add new address
      // If this is marked as primary, update all others to non-primary
      if (newAddress.isPrimary) {
        updatedAddresses = [...addresses.map(addr => ({
          ...addr,
          isPrimary: false
        })), newAddress];
      } else if (addresses.length === 0) {
        // First address should be primary by default
        updatedAddresses = [{
          ...newAddress,
          isPrimary: true
        }];
      } else {
        updatedAddresses = [...addresses, newAddress];
      }
    }
    setAddresses(updatedAddresses);
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: true
    });
    setShowAddressForm(false);
    setEditingAddress(null);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setFormData({
      ...formData,
      street: address.street,
      city: address.city,
      state: address.state || '',
      postalCode: address.postalCode || '',
      addressCountry: address.country,
      addressType: address.addressType || 'Physical',
      isPrimary: address.isPrimary || false
    });
    setShowAddressForm(true);
  };
  const updateAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const updatedAddress = {
      id: editingAddress.id,
      street: formData.street,
      city: formData.city,
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      country: formData.addressCountry,
      addressType: formData.addressType || 'Physical',
      isPrimary: formData.isPrimary
    };
    // Update existing address
    let updatedAddresses = addresses.map(address => address.id === editingAddress.id ? updatedAddress : address);
    // If this address is now primary, update all others to not be primary
    if (updatedAddress.isPrimary) {
      updatedAddresses = updatedAddresses.map(addr => addr.id === editingAddress.id ? addr : {
        ...addr,
        isPrimary: false
      });
    }
    setAddresses(updatedAddresses);
    // Clear form and exit edit mode
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  const removeAddress = id => {
    const addressToRemove = addresses.find(addr => addr.id === id);
    const wasRemovingPrimary = addressToRemove && addressToRemove.isPrimary;
    const filteredAddresses = addresses.filter(address => address.id !== id);
    // If we removed the primary address and there are other addresses left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAddresses.length > 0) {
      filteredAddresses[0].isPrimary = true;
    }
    setAddresses(filteredAddresses);
  };
  // Bank Account functions
  const handleAddBankAccount = () => {
    if (!formData.bankName || !formData.accountName || !formData.accountNumber) {
      setErrors({
        ...errors,
        bankName: !formData.bankName ? 'Bank name is required' : null,
        accountName: !formData.accountName ? 'Account name is required' : null,
        accountNumber: !formData.accountNumber ? 'Account number is required' : null
      });
      return;
    }
    const newBankAccount = {
      id: editingBankAccount ? editingBankAccount.id : bankAccounts.length + 1,
      bankName: formData.bankName,
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      routingNumber: formData.routingNumber,
      swiftCode: formData.swiftCode,
      iban: formData.iban,
      accountType: formData.accountType,
      isPrimary: formData.isPrimary,
      country: formData.addressCountry || formData.country
    };
    let updatedBankAccounts = [];
    if (editingBankAccount) {
      // Update existing bank account
      updatedBankAccounts = bankAccounts.map(account => account.id === editingBankAccount.id ? newBankAccount : account);
    } else {
      // Add new bank account
      // If this is marked as primary, update all others to non-primary
      if (newBankAccount.isPrimary) {
        updatedBankAccounts = [...bankAccounts.map(account => ({
          ...account,
          isPrimary: false
        })), newBankAccount];
      } else if (bankAccounts.length === 0) {
        // First bank account should be primary by default
        updatedBankAccounts = [{
          ...newBankAccount,
          isPrimary: true
        }];
      } else {
        updatedBankAccounts = [...bankAccounts, newBankAccount];
      }
    }
    setBankAccounts(updatedBankAccounts);
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setShowBankAccountForm(false);
    setEditingBankAccount(null);
  };
  const handleEditBankAccount = account => {
    setEditingBankAccount(account);
    setFormData({
      ...formData,
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber || '',
      swiftCode: account.swiftCode || '',
      iban: account.iban || '',
      accountType: account.accountType || 'Checking',
      isPrimary: account.isPrimary || false
    });
    setShowBankAccountForm(true);
  };
  const removeBankAccount = id => {
    const accountToRemove = bankAccounts.find(account => account.id === id);
    const wasRemovingPrimary = accountToRemove && accountToRemove.isPrimary;
    const filteredAccounts = bankAccounts.filter(account => account.id !== id);
    // If we removed the primary account and there are other accounts left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAccounts.length > 0) {
      filteredAccounts[0].isPrimary = true;
    }
    setBankAccounts(filteredAccounts);
  };
  const cancelBankAccountEdit = () => {
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setEditingBankAccount(null);
    setShowBankAccountForm(false);
  };
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const validateForm = () => {
    const newErrors = {};
    if (onboardingMethod === 'direct') {
      // Validate required fields for direct onboarding
      if (!formData.name) newErrors.name = 'Vendor name is required';
      if (!formData.type) newErrors.type = 'Vendor type is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (contacts.length === 0) newErrors.contacts = 'At least one contact is required';
      if (addresses.length === 0) newErrors.addresses = 'At least one address is required';
      // Validate entity selections if not global
      if (!formData.isGlobal && !formData.orgUnitId) {
        newErrors.orgUnitId = 'An organisation unit must be selected';
      }
      // Validate ESG data if provided
      if (formData.esg && formData.esg.score) {
        const score = parseInt(formData.esg.score);
        if (isNaN(score) || score < 0 || score > 100) {
          newErrors['esg.score'] = 'Score must be a number between 0 and 100';
        }
        if (formData.esg.environmental) {
          const envScore = parseInt(formData.esg.environmental);
          if (isNaN(envScore) || envScore < 0 || envScore > 100) {
            newErrors['esg.environmental'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.social) {
          const socialScore = parseInt(formData.esg.social);
          if (isNaN(socialScore) || socialScore < 0 || socialScore > 100) {
            newErrors['esg.social'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.governance) {
          const govScore = parseInt(formData.esg.governance);
          if (isNaN(govScore) || govScore < 0 || govScore > 100) {
            newErrors['esg.governance'] = 'Score must be a number between 0 and 100';
          }
        }
      }
    } else {
      // Validate required fields for invite
      if (!inviteData.name) newErrors.name = 'Vendor name is required';
      if (!inviteData.email) newErrors.email = 'Email is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    if (onboardingMethod === 'direct') {
      // Prepare data for saving
      const vendorData = {
        ...formData,
        status: 'Active',
        complianceStatus: files.length > 0 ? 'Pending Review' : 'Incomplete',
        contacts,
        addresses,
        bankAccounts,
        documents: files.map((file, index) => ({
          id: index + 1,
          name: file.name,
          type: 'Supporting Document',
          uploadDate: new Date().toISOString().split('T')[0],
          expiryDate: null,
          status: 'Valid'
        })),
        esg: formData.esg.score ? {
          score: parseInt(formData.esg.score),
          environmental: formData.esg.environmental ? parseInt(formData.esg.environmental) : null,
          social: formData.esg.social ? parseInt(formData.esg.social) : null,
          governance: formData.esg.governance ? parseInt(formData.esg.governance) : null,
          summary: formData.esg.summary || '',
          lastUpdated: new Date().toISOString().split('T')[0]
        } : null
      };
      // Call the save handler from parent
      onSave(vendorData);
    } else {
      // Send invitation with availability settings
      const inviteWithAvailability = {
        ...inviteData,
        isGlobal: formData.isGlobal,
        orgUnitId: formData.orgUnitId,
        includeChildOrgUnits: formData.includeChildOrgUnits
      };
      onSendInvite(inviteWithAvailability);
    }
  };
  const cancelAddressEdit = () => {
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  return <div data-id="element-22351">
      <div className="mb-8 flex items-center" data-id="element-22352">
        <button onClick={() => setCurrentView('vendors')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-22353">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-22354" />
        </button>
        <div data-id="element-22355">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-22356">
            Add New Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-22357">
            Create a new vendor record or invite a vendor to onboard themselves
          </p>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22358">
        <div className="px-4 py-5 sm:px-6" data-id="element-22359">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22360">
            Onboarding Method
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-22361">
            Choose how you want to onboard this vendor
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-22362">
          <div className="flex flex-col sm:flex-row sm:space-x-4" data-id="element-22363">
            <div className={`flex-1 p-4 border rounded-lg mb-4 sm:mb-0 cursor-pointer ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('direct')} data-id="element-22364">
              <div className="flex items-center" data-id="element-22365">
                <div className="flex-shrink-0" data-id="element-22366">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-22367">
                    {onboardingMethod === 'direct' && <CheckIcon className="h-3 w-3 text-white" data-id="element-22368" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-22369">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-22370">
                    Direct Entry
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-22371">
                    Enter vendor information directly into the system
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex-1 p-4 border rounded-lg cursor-pointer ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('invite')} data-id="element-22372">
              <div className="flex items-center" data-id="element-22373">
                <div className="flex-shrink-0" data-id="element-22374">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-22375">
                    {onboardingMethod === 'invite' && <CheckIcon className="h-3 w-3 text-white" data-id="element-22376" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-22377">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-22378">
                    Send Invitation
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-22379">
                    Send an invitation to the vendor to complete their own
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {onboardingMethod === 'direct' ? <>
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22380">
            <div className="px-4 py-5 sm:px-6" data-id="element-22381">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22382">
                Basic Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22383">
                Enter vendor details and basic information
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22384">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22385">
                <div className="sm:col-span-3" data-id="element-22386">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-22387">
                    Vendor Name <span className="text-red-500" data-id="element-22388">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22389">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.name ? 'border-red-300' : ''}`} data-id="element-22390" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-22391">{errors.name}</p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22392">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-22393">
                    Vendor Type <span className="text-red-500" data-id="element-22394">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22395">
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.type ? 'border-red-300' : ''}`} data-id="element-22396">
                      <option value="" data-id="element-22397">Select vendor type</option>
                      {vendorTypes.map(type => <option key={type} value={type} data-id="element-22398">
                          {type}
                        </option>)}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600" data-id="element-22399">{errors.type}</p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-22400">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-22401">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-22402">
                    <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22403" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22404">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-22405">
                    Country <span className="text-red-500" data-id="element-22406">*</span>
                  </label>
                  <div className="mt-1" data-id="element-22407">
                    <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-22408">
                      <option value="" data-id="element-22409">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-22410">
                          {country}
                        </option>)}
                    </select>
                    {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-22411">
                        {errors.country}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-22412">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-22413">
                    Tax ID / Business Number
                  </label>
                  <div className="mt-1" data-id="element-22414">
                    <input type="text" name="taxId" id="taxId" value={formData.taxId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22415" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-22416">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-22417">
                    Website
                  </label>
                  <div className="mt-1" data-id="element-22418">
                    <input type="url" name="website" id="website" value={formData.website} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22419" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ESG Information */}
          {isESGEnabled && hasESGEditPermission && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22420">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22421">
                <div data-id="element-22422">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22423">
                    ESG Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-22424">
                    Environmental, Social, and Governance data
                  </p>
                </div>
                <button type="button" onClick={() => setShowESGForm(!showESGForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22425">
                  {showESGForm ? <>
                      <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22426" />
                      Hide ESG Form
                    </> : <>
                      <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-22427" />
                      Add ESG Data
                    </>}
                </button>
              </div>
              {showESGForm && <div className="border-t border-gray-200 px-4 py-5" data-id="element-22428">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22429">
                    <div className="sm:col-span-2" data-id="element-22430">
                      <label htmlFor="esg.score" className="block text-sm font-medium text-gray-700" data-id="element-22431">
                        Overall ESG Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22432">
                        <input type="number" name="esg.score" id="esg.score" min="0" max="100" value={formData.esg.score} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.score'] ? 'border-red-300' : ''}`} data-id="element-22433" />
                        {errors['esg.score'] && <p className="mt-1 text-sm text-red-600" data-id="element-22434">
                            {errors['esg.score']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-22435">
                      <label htmlFor="esg.environmental" className="block text-sm font-medium text-gray-700" data-id="element-22436">
                        Environmental Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22437">
                        <input type="number" name="esg.environmental" id="esg.environmental" min="0" max="100" value={formData.esg.environmental} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.environmental'] ? 'border-red-300' : ''}`} data-id="element-22438" />
                        {errors['esg.environmental'] && <p className="mt-1 text-sm text-red-600" data-id="element-22439">
                            {errors['esg.environmental']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-22440">
                      <label htmlFor="esg.social" className="block text-sm font-medium text-gray-700" data-id="element-22441">
                        Social Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22442">
                        <input type="number" name="esg.social" id="esg.social" min="0" max="100" value={formData.esg.social} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.social'] ? 'border-red-300' : ''}`} data-id="element-22443" />
                        {errors['esg.social'] && <p className="mt-1 text-sm text-red-600" data-id="element-22444">
                            {errors['esg.social']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-3" data-id="element-22445">
                      <label htmlFor="esg.governance" className="block text-sm font-medium text-gray-700" data-id="element-22446">
                        Governance Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-22447">
                        <input type="number" name="esg.governance" id="esg.governance" min="0" max="100" value={formData.esg.governance} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.governance'] ? 'border-red-300' : ''}`} data-id="element-22448" />
                        {errors['esg.governance'] && <p className="mt-1 text-sm text-red-600" data-id="element-22449">
                            {errors['esg.governance']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-6" data-id="element-22450">
                      <label htmlFor="esg.summary" className="block text-sm font-medium text-gray-700" data-id="element-22451">
                        ESG Summary
                      </label>
                      <div className="mt-1" data-id="element-22452">
                        <textarea id="esg.summary" name="esg.summary" rows={3} value={formData.esg.summary} onChange={handleInputChange} placeholder="Brief summary of vendor's ESG initiatives and performance" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22453" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
          {/* Contact Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22454">
            <div className="px-4 py-5 sm:px-6" data-id="element-22455">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22456">
                Contact Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22457">
                Add primary and additional contacts for this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22458">
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22459">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22460">
                  <h4 className="text-base font-medium text-gray-900" data-id="element-22461">
                    Add Contact
                  </h4>
                </div>
                <div className="p-4" data-id="element-22462">
                  <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22463">
                    <div data-id="element-22464">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22465">
                        Contact Name <span className="text-red-500" data-id="element-22466">*</span>
                      </label>
                      <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.contactName ? 'border-red-300' : ''}`} value={formData.contactName} name="contactName" onChange={handleInputChange} placeholder="Full name" data-id="element-22467" />
                      {errors.contactName && <p className="mt-1 text-sm text-red-600" data-id="element-22468">
                          {errors.contactName}
                        </p>}
                    </div>
                    <div data-id="element-22469">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22470">
                        Email <span className="text-red-500" data-id="element-22471">*</span>
                      </label>
                      <input type="email" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.email ? 'border-red-300' : ''}`} value={formData.email} name="email" onChange={handleInputChange} placeholder="Email address" data-id="element-22472" />
                      {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-22473">
                          {errors.email}
                        </p>}
                    </div>
                    <div data-id="element-22474">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22475">
                        Phone
                      </label>
                      <input type="tel" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.phone} name="phone" onChange={handleInputChange} placeholder="Phone number" data-id="element-22476" />
                    </div>
                    <div data-id="element-22477">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22478">
                        Role
                      </label>
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.role} name="role" onChange={handleInputChange} placeholder="Job title or role" data-id="element-22479" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-22480">
                    <button type="button" onClick={addContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22481">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-22482" />
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
              {errors.contacts && <div className="mb-4" data-id="element-22483">
                  <p className="text-sm text-red-600" data-id="element-22484">{errors.contacts}</p>
                </div>}
              {contacts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-22485">
                  {contacts.map(contact => <li key={contact.id} className="py-4 flex items-center" data-id="element-22486">
                      <div className="flex-shrink-0" data-id="element-22487">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500" data-id="element-22488">
                          <UserPlusIcon className="h-5 w-5" data-id="element-22489" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1" data-id="element-22490">
                        <p className="text-sm font-medium text-gray-900" data-id="element-22491">
                          {contact.name}
                        </p>
                        <div className="flex items-center text-sm text-gray-500" data-id="element-22492">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-22493" />
                          {contact.email}
                        </div>
                        {contact.phone && <p className="mt-1 text-sm text-gray-500" data-id="element-22494">
                            {contact.phone}
                          </p>}
                        {contact.role && <p className="mt-1 text-sm text-gray-500" data-id="element-22495">
                            {contact.role}
                          </p>}
                      </div>
                      <div className="ml-4" data-id="element-22496">
                        <button type="button" onClick={() => removeContact(contact.id)} className="text-red-600 hover:text-red-900" data-id="element-22497">
                          <TrashIcon className="h-5 w-5" data-id="element-22498" />
                        </button>
                      </div>
                    </li>)}
                </ul> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22499">
                  No contacts added yet. Add at least one contact.
                </div>}
            </div>
          </div>
          {/* Address Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22500">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22501">
              <div data-id="element-22502">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22503">
                  Address Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-22504">
                  Add addresses for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowAddressForm(!showAddressForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22505">
                {showAddressForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22506" />
                    Hide Address Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-22507" />
                    Add Address
                  </>}
              </button>
            </div>
            {showAddressForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-22508">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22509">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22510">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-22511">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-22512">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22513">
                      <div data-id="element-22514">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22515">
                          Street Address <span className="text-red-500" data-id="element-22516">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.street ? 'border-red-300' : ''}`} value={formData.street} name="street" onChange={handleInputChange} placeholder="Street address" data-id="element-22517" />
                        {errors.street && <p className="mt-1 text-sm text-red-600" data-id="element-22518">
                            {errors.street}
                          </p>}
                      </div>
                      <div data-id="element-22519">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22520">
                          City <span className="text-red-500" data-id="element-22521">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.city ? 'border-red-300' : ''}`} value={formData.city} name="city" onChange={handleInputChange} placeholder="City" data-id="element-22522" />
                        {errors.city && <p className="mt-1 text-sm text-red-600" data-id="element-22523">
                            {errors.city}
                          </p>}
                      </div>
                      <div data-id="element-22524">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22525">
                          State/Province
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.state} name="state" onChange={handleInputChange} placeholder="State or province" data-id="element-22526" />
                      </div>
                      <div data-id="element-22527">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22528">
                          Postal Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.postalCode} name="postalCode" onChange={handleInputChange} placeholder="Postal code" data-id="element-22529" />
                      </div>
                      <div data-id="element-22530">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22531">
                          Country <span className="text-red-500" data-id="element-22532">*</span>
                        </label>
                        <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.addressCountry ? 'border-red-300' : ''}`} value={formData.addressCountry} name="addressCountry" onChange={handleInputChange} data-id="element-22533">
                          <option value="" data-id="element-22534">Select country</option>
                          {countries.map(country => <option key={country} value={country} data-id="element-22535">
                              {country}
                            </option>)}
                        </select>
                        {errors.addressCountry && <p className="mt-1 text-sm text-red-600" data-id="element-22536">
                            {errors.addressCountry}
                          </p>}
                      </div>
                      <div data-id="element-22537">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22538">
                          Address Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.addressType} name="addressType" onChange={handleInputChange} data-id="element-22539">
                          {addressTypes.map(type => <option key={type} value={type} data-id="element-22540">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-22541">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22542" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-22543">
                          Primary Address
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-22544">
                      <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22545">
                        {editingAddress ? 'Update Address' : 'Add Address'}
                      </button>
                      <button type="button" onClick={() => {
                  setShowAddressForm(false);
                  setEditingAddress(null);
                }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22546">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22547">
              {errors.addresses && <div className="mb-4" data-id="element-22548">
                  <p className="text-sm text-red-600" data-id="element-22549">{errors.addresses}</p>
                </div>}
              {addresses.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-22550">
                  {addresses.map(address => <div key={address.id} className={`relative rounded-lg border ${address.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-22551">
                      {address.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-22552">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-22553" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-22554">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-22555">
                          <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-22556" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-22557">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-22558">
                            {address.addressType}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-22559">
                        <p data-id="element-22560">{address.street}</p>
                        <p data-id="element-22561">
                          {address.city}
                          {address.state && `, ${address.state}`}{' '}
                          {address.postalCode}
                        </p>
                        <p data-id="element-22562">{address.country}</p>
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-22563">
                        <button type="button" onClick={() => handleEditAddress(address)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-22564">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-22565">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22566">
                  No addresses added yet. Add at least one address.
                </div>}
            </div>
          </div>
          {/* Bank Account Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22567">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-22568">
              <div data-id="element-22569">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22570">
                  Bank Account Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-22571">
                  Add payment bank accounts for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowBankAccountForm(!showBankAccountForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22572">
                {showBankAccountForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-22573" />
                    Hide Bank Account Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-22574" />
                    Add Bank Account
                  </>}
              </button>
            </div>
            {showBankAccountForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-22575">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-22576">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-22577">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-22578">
                      {editingBankAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-22579">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-22580">
                      <div data-id="element-22581">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22582">
                          Bank Name <span className="text-red-500" data-id="element-22583">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.bankName ? 'border-red-300' : ''}`} value={formData.bankName} name="bankName" onChange={handleInputChange} placeholder="Bank name" data-id="element-22584" />
                        {errors.bankName && <p className="mt-1 text-sm text-red-600" data-id="element-22585">
                            {errors.bankName}
                          </p>}
                      </div>
                      <div data-id="element-22586">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22587">
                          Account Name <span className="text-red-500" data-id="element-22588">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountName ? 'border-red-300' : ''}`} value={formData.accountName} name="accountName" onChange={handleInputChange} placeholder="Account holder name" data-id="element-22589" />
                        {errors.accountName && <p className="mt-1 text-sm text-red-600" data-id="element-22590">
                            {errors.accountName}
                          </p>}
                      </div>
                      <div data-id="element-22591">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22592">
                          Account Number <span className="text-red-500" data-id="element-22593">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountNumber ? 'border-red-300' : ''}`} value={formData.accountNumber} name="accountNumber" onChange={handleInputChange} placeholder="Account number" data-id="element-22594" />
                        {errors.accountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-22595">
                            {errors.accountNumber}
                          </p>}
                      </div>
                      <div data-id="element-22596">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22597">
                          Routing/BSB/Sort Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.routingNumber} name="routingNumber" onChange={handleInputChange} placeholder="Routing/BSB/Sort code" data-id="element-22598" />
                      </div>
                      <div data-id="element-22599">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22600">
                          SWIFT/BIC Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.swiftCode} name="swiftCode" onChange={handleInputChange} placeholder="SWIFT/BIC code" data-id="element-22601" />
                      </div>
                      <div data-id="element-22602">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22603">
                          IBAN
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.iban} name="iban" onChange={handleInputChange} placeholder="IBAN (if applicable)" data-id="element-22604" />
                      </div>
                      <div data-id="element-22605">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22606">
                          Account Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.accountType} name="accountType" onChange={handleInputChange} data-id="element-22607">
                          {accountTypes.map(type => <option key={type} value={type} data-id="element-22608">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-22609">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22610" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-22611">
                          Primary Bank Account
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-22612">
                      <button type="button" onClick={handleAddBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22613">
                        {editingBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
                      </button>
                      <button type="button" onClick={cancelBankAccountEdit} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22614">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22615">
              {bankAccounts.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-22616">
                  {bankAccounts.map(account => <div key={account.id} className={`relative rounded-lg border ${account.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-22617">
                      {account.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-22618">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-22619" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-22620">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-22621">
                          <div className="h-5 w-5 text-gray-500" data-id="element-22622" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-22623">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-22624">
                            {account.bankName}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-22625">
                        <p data-id="element-22626">Account Name: {account.accountName}</p>
                        <p data-id="element-22627">
                          Account Number: •••• {account.accountNumber.slice(-4)}
                        </p>
                        <p data-id="element-22628">Account Type: {account.accountType}</p>
                        {account.routingNumber && <p data-id="element-22629">Routing: {account.routingNumber}</p>}
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-22630">
                        <button type="button" onClick={() => handleEditBankAccount(account)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-22631">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeBankAccount(account.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-22632">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-22633">
                  No bank accounts added yet. Add at least one bank account for
                  payment processing.
                </div>}
            </div>
          </div>
          {/* Organisation Unit Availability */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22634">
            <div className="px-4 py-5 sm:px-6" data-id="element-22635">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22636">
                Organisation Unit Availability
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22637">
                Define which organisation units can use this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22638">
              <div className="flex items-center mb-4" data-id="element-22639">
                <input id="isGlobal" name="isGlobal" type="checkbox" checked={formData.isGlobal} onChange={e => handleAvailabilityChange(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-22640" />
                <label htmlFor="isGlobal" className="ml-2 block text-sm text-gray-700" data-id="element-22641">
                  Global Vendor (available to all entities)
                </label>
              </div>
              {!formData.isGlobal && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-22642">
                  <div className="sm:col-span-4" data-id="element-22643">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22644">
                      Organisation Unit <span className="text-red-500" data-id="element-22645">*</span>
                    </label>
                    <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.orgUnitId ? 'border-red-300' : ''}`} name="orgUnitId" value={formData.orgUnitId || ''} onChange={handleInputChange} data-id="element-22646">
                      <option value="" data-id="element-22647">Select an organisation unit</option>
                      {orgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-22648">
                          {orgUnit.path} - {orgUnit.name}
                        </option>)}
                    </select>
                    {errors.orgUnitId && <p className="mt-1 text-sm text-red-600" data-id="element-22649">
                        {errors.orgUnitId}
                      </p>}
                  </div>
                  <div className="sm:col-span-2" data-id="element-22650">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-22651">
                      Include Children
                    </label>
                    <div className="flex items-center h-10" data-id="element-22652">
                      <input type="checkbox" name="includeChildOrgUnits" id="includeChildOrgUnits" checked={formData.includeChildOrgUnits || false} onChange={handleInputChange} disabled={!formData.orgUnitId} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50" data-id="element-22653" />
                      <label htmlFor="includeChildOrgUnits" className="ml-2 text-sm text-gray-700" data-id="element-22654">
                        Include child org units
                      </label>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Documents */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22655">
            <div className="px-4 py-5 sm:px-6" data-id="element-22656">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22657">
                Documents
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22658">
                Upload supporting documents
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-22659">
              <FileUpload onFileUpload={handleFileUpload} data-id="element-22660" />
              {files.length > 0 && <div className="mt-4" data-id="element-22661">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-22662">
                    Files to Upload
                  </h4>
                  <ul className="border rounded-md divide-y divide-gray-200" data-id="element-22663">
                    {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-22664">
                        <div className="w-0 flex-1 flex items-center" data-id="element-22665">
                          <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-22666">
                            <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" data-id="element-22667" />
                          </svg>
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-22668">
                            {file.name}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-22669">
                          <button type="button" onClick={() => removeFile(index)} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-22670">
                            Remove
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-22671">
            <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22672">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22673">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-22674" />
              Save Vendor
            </button>
          </div>
        </> :
    // Send Invitation Form
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-22675">
          <div className="px-4 py-5 sm:px-6" data-id="element-22676">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22677">
              Send Vendor Invitation
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-22678">
              Invite a vendor to complete their own onboarding
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-22679">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22680">
              <div className="sm:col-span-3" data-id="element-22681">
                <label htmlFor="inviteName" className="block text-sm font-medium text-gray-700" data-id="element-22682">
                  Vendor Name <span className="text-red-500" data-id="element-22683">*</span>
                </label>
                <div className="mt-1" data-id="element-22684">
                  <input type="text" name="name" id="inviteName" value={inviteData.name} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteName ? 'border-red-300' : ''}`} data-id="element-22685" />
                  {errors.inviteName && <p className="mt-1 text-sm text-red-600" data-id="element-22686">
                      {errors.inviteName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-22687">
                <label htmlFor="inviteType" className="block text-sm font-medium text-gray-700" data-id="element-22688">
                  Vendor Type
                </label>
                <div className="mt-1" data-id="element-22689">
                  <select id="inviteType" name="type" value={inviteData.type} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22690">
                    <option value="" data-id="element-22691">Select vendor type</option>
                    {vendorTypes.map(type => <option key={type} value={type} data-id="element-22692">
                        {type}
                      </option>)}
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-22693">
                <label htmlFor="inviteContactName" className="block text-sm font-medium text-gray-700" data-id="element-22694">
                  Contact Name <span className="text-red-500" data-id="element-22695">*</span>
                </label>
                <div className="mt-1" data-id="element-22696">
                  <input type="text" name="contactName" id="inviteContactName" value={inviteData.contactName} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteContactName ? 'border-red-300' : ''}`} data-id="element-22697" />
                  {errors.inviteContactName && <p className="mt-1 text-sm text-red-600" data-id="element-22698">
                      {errors.inviteContactName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-22699">
                <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700" data-id="element-22700">
                  Email <span className="text-red-500" data-id="element-22701">*</span>
                </label>
                <div className="mt-1" data-id="element-22702">
                  <input type="email" name="email" id="inviteEmail" value={inviteData.email} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteEmail ? 'border-red-300' : ''}`} data-id="element-22703" />
                  {errors.inviteEmail && <p className="mt-1 text-sm text-red-600" data-id="element-22704">
                      {errors.inviteEmail}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-22705">
                <label htmlFor="invitePhone" className="block text-sm font-medium text-gray-700" data-id="element-22706">
                  Phone
                </label>
                <div className="mt-1" data-id="element-22707">
                  <input type="tel" name="phone" id="invitePhone" value={inviteData.phone} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22708" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-22709">
                <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700" data-id="element-22710">
                  Role
                </label>
                <div className="mt-1" data-id="element-22711">
                  <input type="text" name="role" id="inviteRole" value={inviteData.role} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22712" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-22713">
                <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700" data-id="element-22714">
                  Invitation Message
                </label>
                <div className="mt-1" data-id="element-22715">
                  <textarea id="inviteMessage" name="message" rows={4} value={inviteData.message} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-22716" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-22717">
              <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22718">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22719">
                <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-22720" />
                Send Invitation
              </button>
            </div>
          </div>
        </div>}
    </div>;
}